import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, useUser, useIsHydrated } from '@aegix/auth-identity';
import { Spinner, Center } from '@chakra-ui/react';
import type { SMSUser } from '../lib/sms-auth';

interface PrivateRouteProps {
  children: React.ReactNode;
}

/**
 * Route gate. Renders children when the user is authenticated;
 * shows a spinner while the auth provider is still reconciling at
 * boot; redirects to `/login` otherwise.
 *
 * The spinner gates on `useIsHydrated()`, NOT on
 * `status === 'anonymous'`. `initialAuthState` is `anonymous`, and a
 * clean/incognito visitor's boot reconcile also *settles* on
 * `anonymous` — so treating `anonymous` as "still loading" would leave a
 * logged-out visitor spinning forever instead of reaching `/login`
 * ("never loads" in a fresh window). `isHydrated` flips true once
 * reconcile has settled (after the `/me` probe for the cookie/token
 * path), which is the correct "done loading" signal.
 */
export function PrivateRoute({ children }: PrivateRouteProps) {
  const { state } = useAuth<SMSUser>();
  const user = useUser<SMSUser>();
  const isHydrated = useIsHydrated();
  const location = useLocation();

  // Boot reconcile (storage/cookie read + any /me probe) still in flight.
  if (!isHydrated) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="button.primary" />
      </Center>
    );
  }

  // Reconciled. Only `authenticated`/`refreshing` (with a user) may pass;
  // everything else — `anonymous`, `logged-out`, `expired`, missing user —
  // goes to login. (`expired` redirects intentionally: identity is known
  // but the credentials are gone.)
  const isAuthenticated =
    !!user &&
    (state.status === 'authenticated' || state.status === 'refreshing');

  if (!isAuthenticated) {
    // Redirect to login, but save the location they were trying to access
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
